import { Facebook, Instagram } from "lucide-react";
import { useScrollReveal } from "../lib/hooks";
import { PageHero, SectionBadge, CTABanner } from "../components/Shared";
import {
  GALLERY, REAL_VIDEO_URL, FACEBOOK_URL, INSTAGRAM_URL,
  FONT_HEAD, FONT_BODY, NAVY, GOLD,
} from "../lib/constants";

const BeforeAfterCell = ({ before, after, span, label, date, idx }) => (
  <div data-testid={`gallery-cell-${idx}`} className={`overflow-hidden group relative ${span}`}>
    <div className="grid grid-cols-2 h-full">
      <div className="relative overflow-hidden">
        <img
          src={before}
          alt={`${label} before professional cleaning in Naples, FL — Cindyrella Cleaning LLC`}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <span
          data-testid="before-badge"
          className="absolute top-2 left-2 px-2.5 py-1 text-[11px] font-bold tracking-widest uppercase"
          style={{ background: "rgba(27,58,107,0.9)", color: "#FFFFFF", fontFamily: FONT_BODY }}
        >
          Before
        </span>
      </div>
      <div className="relative overflow-hidden">
        <img
          src={after}
          alt={`${label} after professional cleaning in Naples, FL — spotless results by Cindyrella Cleaning LLC`}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <span
          data-testid="after-badge"
          className="absolute top-2 right-2 px-2.5 py-1 text-[11px] font-bold tracking-widest uppercase"
          style={{ background: GOLD, color: NAVY, fontFamily: FONT_BODY }}
        >
          After
        </span>
      </div>
    </div>
    <div
      className="absolute bottom-0 left-0 right-0 px-4 py-2.5 flex items-center justify-between"
      style={{ background: "linear-gradient(to top, rgba(27,58,107,0.85), rgba(27,58,107,0.55))" }}
    >
      <span className="text-white text-xs font-semibold" style={{ fontFamily: FONT_BODY }}>
        {label} · Naples, FL
      </span>
      <span className="text-[11px] font-bold" style={{ color: GOLD, fontFamily: FONT_BODY }}>
        {date}
      </span>
    </div>
  </div>
);

const GalleryCell = ({ url, span, label, date, idx }) => (
  <div data-testid={`gallery-cell-${idx}`} className={`overflow-hidden group relative ${span}`}>
    <img
      src={url}
      alt={`${label} — professional cleaning results in Naples, FL by Cindyrella Cleaning LLC`}
      loading="lazy"
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
    />
    <span
      data-testid={`gallery-date-${idx}`}
      className="absolute top-2 left-2 px-2.5 py-1 text-[11px] font-bold tracking-wide"
      style={{ background: "rgba(27,58,107,0.85)", color: GOLD, fontFamily: FONT_BODY, backdropFilter: "blur(4px)" }}
    >
      {date}
    </span>
    <div
      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4"
      style={{ background: "linear-gradient(to top, rgba(27,58,107,0.75), transparent)" }}
    >
      <span className="text-white text-xs font-semibold" style={{ fontFamily: FONT_BODY }}>
        {label} · Naples, FL · {date}
      </span>
    </div>
  </div>
);

const GalleryGrid = () => {
  const ref = useScrollReveal();
  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      ref={ref}
      className="py-16 md:py-24 section-hidden"
      style={{ background: "#F9FAFB" }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <SectionBadge>Our Portfolio</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
            Our Work Speaks for Itself
          </h2>
          <p className="mt-4 text-base max-w-xl mx-auto" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
            Every space we clean becomes a showcase of our commitment to
            excellence in Naples, FL.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 auto-rows-[180px] md:auto-rows-[220px] gap-3">
          {GALLERY.map((cell, idx) =>
            cell.type === "beforeAfter" ? (
              <BeforeAfterCell key={cell.label} {...cell} idx={idx} />
            ) : (
              <GalleryCell key={cell.label} {...cell} idx={idx} />
            )
          )}
        </div>
      </div>
    </section>
  );
};

const VideoCard = ({ src, poster, title, description, loop, testid }) => (
  <div data-testid={testid} className="overflow-hidden" style={{ border: "1px solid rgba(27,58,107,0.1)" }}>
    <video
      src={src}
      controls
      playsInline
      loop={loop}
      poster={poster}
      className="w-full"
      style={{ display: "block", maxHeight: "320px", objectFit: "cover", background: NAVY }}
      aria-label={title}
    />
    <div className="p-5">
      <h3 className="text-base font-bold mb-1" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
        {title}
      </h3>
      <p className="text-sm leading-relaxed" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
        {description}
      </p>
    </div>
  </div>
);

const TeamVideoCard = () => (
  <VideoCard
    testid="video-card-0"
    src={REAL_VIDEO_URL}
    poster="https://images.unsplash.com/photo-1583907659441-addbe699e921?crop=entropy&cs=srgb&fm=webp&q=70&w=800"
    title="Our Team in Action"
    description="Watch how our professional team transforms spaces with precision and care."
  />
);

const FanDetailVideoCard = () => (
  <VideoCard
    testid="video-card-2"
    src="/media/team-cleaning-2.mp4"
    poster="/media/team-cleaning-2-poster.jpg"
    loop
    title="Ceiling Fan Detail Cleaning"
    description="No detail is too high — outdoor lanai fans and fixtures, spotless."
  />
);

const FacebookVideoCard = () => (
  <a
    href="https://www.facebook.com/share/r/1GpGRCXVoY/"
    target="_blank"
    rel="noopener noreferrer"
    data-testid="video-card-1"
    className="group block overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
    style={{ border: "1px solid rgba(27,58,107,0.1)" }}
  >
    <div
      className="relative flex items-center justify-center"
      style={{ background: "linear-gradient(135deg, #1B3A6B 0%, #152d53 100%)", height: "240px" }}
    >
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 50%, #D4A843 0%, transparent 50%), radial-gradient(circle at 80% 20%, #ffffff 0%, transparent 40%)",
        }}
      />
      <div className="relative z-10 flex flex-col items-center gap-4">
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
          style={{ background: "rgba(255,255,255,0.15)", border: `3px solid ${GOLD}`, backdropFilter: "blur(8px)" }}
        >
          <div style={{ width: 0, height: 0, borderTop: "14px solid transparent", borderBottom: "14px solid transparent", borderLeft: `22px solid ${GOLD}`, marginLeft: "4px" }} />
        </div>
        <div className="flex items-center gap-2 px-4 py-1.5" style={{ background: "rgba(255,255,255,0.12)", backdropFilter: "blur(6px)" }}>
          <Facebook size={14} style={{ color: GOLD }} />
          <span className="text-xs font-bold tracking-wide" style={{ color: "#FFFFFF", fontFamily: FONT_BODY }}>
            Watch on Facebook
          </span>
        </div>
      </div>
    </div>
    <div className="p-5" style={{ background: "#FFFFFF" }}>
      <h3 className="text-base font-bold mb-1" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
        Professional Results
      </h3>
      <p className="text-sm leading-relaxed" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
        See the before &amp; after — the Cindyrella standard in full display.
      </p>
    </div>
  </a>
);

const VideosSection = () => {
  const ref = useScrollReveal();
  return (
    <section
      id="videos"
      data-testid="videos-section"
      ref={ref}
      className="py-16 md:py-24 section-hidden"
      style={{ background: "#FFFFFF" }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <SectionBadge>Watch Us Work</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
            See the Cindyrella Difference
          </h2>
          <p className="mt-4 text-base max-w-xl mx-auto" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
            Watch our team deliver the five-star results Naples clients love.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
          <TeamVideoCard />
          <FanDetailVideoCard />
          <FacebookVideoCard />
        </div>
        <div className="text-center">
          <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="videos-facebook-link"
            className="inline-flex items-center gap-3 px-8 py-4 font-bold text-base transition-all duration-300 hover:opacity-90 hover:shadow-lg"
            style={{ background: NAVY, color: "#FFFFFF", fontFamily: FONT_BODY }}
          >
            <Facebook size={18} />
            See More Videos on Facebook
          </a>
        </div>
      </div>
    </section>
  );
};

const IG_PHOTOS = GALLERY.map((g) => ({ url: g.url || g.after, label: g.label })).slice(0, 6);

const InstagramTile = ({ url, label, idx }) => (
  <a
    href={INSTAGRAM_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`instagram-tile-${idx}`}
    className="group relative block overflow-hidden aspect-square"
    aria-label={`${label} — see more on Instagram`}
  >
    <img
      src={url}
      alt={`${label} — Cindyrella Cleaning on Instagram`}
      loading="lazy"
      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
    />
    <div
      className="absolute inset-0 flex flex-col items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
      style={{ background: "rgba(27,58,107,0.75)" }}
    >
      <Instagram size={26} style={{ color: GOLD }} />
      <span className="text-xs font-bold text-white px-3 text-center" style={{ fontFamily: FONT_BODY }}>
        {label}
      </span>
    </div>
  </a>
);

const InstagramSection = () => {
  const ref = useScrollReveal();
  return (
    <section
      id="instagram"
      data-testid="instagram-section"
      ref={ref}
      className="py-16 md:py-24 section-hidden"
      style={{ background: "#F9FAFB" }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <SectionBadge>Follow Our Work</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
            Fresh From Our Instagram
          </h2>
          <p className="mt-4 text-base max-w-xl mx-auto" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
            Sparkling results, behind-the-scenes moments, and cleaning tips —
            follow <span className="font-bold" style={{ color: NAVY }}>@cindyrella_cleaning</span> to see our latest work.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
          {IG_PHOTOS.map((p, idx) => (
            <InstagramTile key={p.label} {...p} idx={idx} />
          ))}
        </div>
        <div className="text-center mt-10">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-follow-btn"
            className="inline-flex items-center gap-3 px-8 py-4 font-bold text-base transition-all duration-300 hover:opacity-90 hover:shadow-lg"
            style={{ background: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)", color: "#FFFFFF", fontFamily: FONT_BODY }}
          >
            <Instagram size={18} />
            Follow @cindyrella_cleaning
          </a>
        </div>
      </div>
    </section>
  );
};

export default function GalleryPage() {
  return (
    <>
      <PageHero
        testid="gallery-page-hero"
        badge="Our Portfolio"
        title={<>Results That <span style={{ color: GOLD }}>Shine</span></>}
        subtitle="A look at the standard we bring to every home, rental, and office in Naples."
      />
      <GalleryGrid />
      <VideosSection />
      <InstagramSection />
      <CTABanner testid="gallery-cta-banner" />
    </>
  );
}
